import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { currentMonthKey } from './arBillingDates';

/**
 * El panel de facturación AR de una empresa: los euros que tiene en gestión
 * este mes, que es lo que fija su tramo (06-reparto §5.5).
 *
 *   gestionado = lo activo el día 1 + lo que entra durante el mes
 *
 * Y, en los últimos días del mes, el aviso de lo que se volverá a facturar el
 * día 1 si no da nada de baja. Es lo que hace que pagar el arrastre se vea
 * justo: lo controla él.
 *
 * Y lo que se va a facturar: el precio fijo del tramo en el que cae el total,
 * con la tabla que ClaimUp tiene definida para la empresa (sin IVA). Un mes
 * cerrado, con la que había al cerrarlo. Ver docs/ar-precios-tramos-plan.md.
 */

export const DIAS_DE_AVISO = 5;

const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

const fecha = iso =>
  new Date(iso).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit' });

/** Días que quedan para el cierre del mes, en hora de Madrid (hoy incluido). */
export const diasParaElCierre = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = type => Number(parts.find(p => p.type === type).value);
  const diasDelMes = new Date(Date.UTC(get('year'), get('month'), 0)).getUTCDate();
  return diasDelMes - get('day') + 1;
};

const KIND_LABELS = {
  debt: 'Deuda nueva',
  reactivation: 'Reactivado',
  type_in: 'Pasado a AR',
};

function CompanyArBilling({ companyId, now = null }) {
  // `now` solo lo pasan los tests: el aviso depende del día del mes.
  const ahora = now || new Date();
  const mesActual = currentMonthKey(ahora);
  const [month, setMonth] = useState(mesActual);
  const [data, setData] = useState(null);
  const [sinAr, setSinAr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verArrastre, setVerArrastre] = useState(false);

  useEffect(() => {
    setLoading(true);
    const ruta =
      month === mesActual
        ? `/ar-billing/companies/${companyId}/current`
        : `/ar-billing/companies/${companyId}/months/${month}`;
    api
      .get(ruta)
      .then(({ data: res }) => {
        setData(res.data);
        setSinAr(false);
      })
      .catch(err => {
        setData(null);
        // 404 en el mes actual = la empresa no tiene AR. En un mes pasado,
        // que ese mes no tiene datos.
        if (err?.response?.status === 404 && month === mesActual) setSinAr(true);
        else if (err?.response?.status !== 404) toast.error(getErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, month, mesActual]);

  const nombre = id => data?.debtor_names?.[id] || 'Expediente';
  const enCurso = data && !data.closed && month === mesActual;
  const dias = diasParaElCierre(ahora);

  return (
    <div className='space-y-4'>
      <header className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h2 className='font-semibold text-slate-800'>AR: euros gestionados</h2>
          <p className='text-sm text-slate-500 mt-1'>
            Los euros que tenéis en gestión en el mes: lo que seguía activo el día 1 más todo lo que ha entrado
            desde entonces. Pagos y bajas no restan hasta el mes siguiente.
          </p>
        </div>
        <label className='text-sm text-slate-600 flex items-center gap-2'>
          Mes
          <input
            type='month'
            aria-label='Mes'
            value={month}
            max={mesActual}
            onChange={e => e.target.value && setMonth(e.target.value)}
            className='form-input rounded-lg border-slate-300 text-sm'
          />
        </label>
      </header>

      {loading && <p className='text-sm text-slate-500'>Cargando…</p>}

      {!loading && sinAr && (
        <p className='bg-white border border-slate-200 rounded-lg px-5 py-6 text-sm text-slate-500'>
          Vuestra empresa no tiene el servicio AR activado.
        </p>
      )}

      {!loading && !sinAr && !data && (
        <p className='bg-white border border-slate-200 rounded-lg px-5 py-6 text-sm text-slate-500'>
          No hay datos de ese mes.
        </p>
      )}

      {!loading && data && (
        <>
          {enCurso && dias <= DIAS_DE_AVISO && data.next_opening_forecast && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900' role='alert'>
              <p className='font-semibold'>
                {dias === 1 ? 'Hoy cierra el mes.' : `Quedan ${dias} días para el cierre del mes.`}
              </p>
              <p className='mt-1'>
                El día 1 arrancaréis con <strong>{eur(data.next_opening_forecast.total)}</strong> en gestión (
                {data.next_opening_forecast.count} expedientes que siguen activos)
                {data.next_opening_forecast.amount !== null && data.next_opening_forecast.amount !== undefined && (
                  <>
                    , que ya son <strong>{eur(data.next_opening_forecast.amount)}</strong> al mes sin IVA
                  </>
                )}
                . Si hay alguno que no queréis seguir gestionando, dadlo de baja antes del cierre.
              </p>
            </div>
          )}

          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            <Stat
              label='Activo el día 1'
              value={eur(data.opening.total)}
              hint={`${data.opening.items.length} expedientes`}
            />
            <Stat
              label='Nuevo en el mes'
              value={eur(data.entries.total)}
              hint={`${data.entries.items.length} entradas`}
            />
            <Stat
              label={data.closed ? 'Total gestionado · cerrado' : 'Total gestionado · en curso'}
              value={eur(data.total)}
              strong
            />
          </div>

          <Billing billing={data.billing} closed={data.closed} />

          <section className='bg-white border border-slate-200 rounded-lg overflow-x-auto'>
            <h2 className='px-5 pt-4 font-semibold text-slate-800'>Lo que ha entrado este mes</h2>
            {data.entries.items.length === 0 ? (
              <p className='px-5 py-4 text-sm text-slate-500'>Todavía no ha entrado nada este mes.</p>
            ) : (
              <table className='min-w-full text-sm mt-2'>
                <thead className='bg-slate-50 text-slate-500 text-xs uppercase'>
                  <tr>
                    <th className='text-left px-4 py-2 font-semibold'>Fecha</th>
                    <th className='text-left px-4 py-2 font-semibold'>Deudor</th>
                    <th className='text-left px-4 py-2 font-semibold'>Qué</th>
                    <th className='text-right px-4 py-2 font-semibold'>Importe</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {data.entries.items.map((i, k) => (
                    <tr key={`${i.expedient_id}-${k}`}>
                      <td className='px-4 py-2 tabular-nums'>{fecha(i.at)}</td>
                      <td className='px-4 py-2'>
                        <Link to={`/view/${i.expedient_id}`} className='text-indigo-600 hover:text-indigo-800'>
                          {nombre(i.expedient_id)}
                        </Link>
                      </td>
                      <td className='px-4 py-2 text-slate-600'>{KIND_LABELS[i.kind] || i.kind}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className='bg-white border border-slate-200 rounded-lg'>
            <button
              type='button'
              onClick={() => setVerArrastre(v => !v)}
              className='w-full text-left px-5 py-4 font-semibold text-slate-800 flex justify-between'
            >
              <span>Lo que seguía activo el día 1 ({data.opening.items.length})</span>
              <span className='text-sm text-indigo-600'>{verArrastre ? 'Ocultar' : 'Ver'}</span>
            </button>
            {verArrastre && (
              <table className='min-w-full text-sm'>
                <tbody className='divide-y divide-slate-100'>
                  {data.opening.items.map(i => (
                    <tr key={i.expedient_id}>
                      <td className='px-4 py-2'>
                        <Link to={`/view/${i.expedient_id}`} className='text-indigo-600 hover:text-indigo-800'>
                          {nombre(i.expedient_id)}
                        </Link>
                      </td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(i.importe_deuda)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** Lo que se factura por el mes, o por qué no hay importe. */
export function Billing({ billing, closed }) {
  if (!billing) {
    return (
      <div className='bg-white border border-slate-200 rounded-lg px-5 py-4 text-sm text-slate-500'>
        {closed
          ? 'Este mes se cerró sin tarifa definida.'
          : 'Tarifa sin definir: ClaimUp todavía no ha fijado vuestros precios de AR.'}
      </div>
    );
  }
  if (billing.above_last) {
    return (
      <div className='bg-white border border-amber-200 bg-amber-50 rounded-lg px-5 py-4 text-sm text-amber-900'>
        El total gestionado pasa del último tramo de vuestra tarifa ({eur(billing.from)}). ClaimUp os dirá el precio.
      </div>
    );
  }
  return (
    <div className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-4'>
      <div>
        <div className='text-xs text-slate-500'>{closed ? 'Facturado este mes' : 'A facturar este mes'} · sin IVA</div>
        <div className='mt-1 text-2xl font-semibold text-slate-900 tabular-nums'>{eur(billing.amount)}</div>
        <div className='text-xs text-slate-400 mt-0.5'>
          + IVA (21 %) {eur(billing.vat)} = {eur(billing.amount_with_vat)}
        </div>
      </div>
      <div className='text-sm text-slate-600 text-right'>
        <div>
          Tramo de {eur(billing.from)} {billing.to === null ? 'en adelante' : `a ${eur(billing.to)}`}
        </div>
        {!closed && billing.next && (
          <div className='text-xs text-slate-500 mt-0.5'>
            Faltan {eur(billing.next.remaining)} para el siguiente tramo ({eur(billing.next.price)} al mes)
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint, strong }) {
  return (
    <div className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
      <div className='text-xs text-slate-500'>{label}</div>
      <div className={`mt-1 tabular-nums ${strong ? 'text-2xl font-semibold text-slate-900' : 'text-xl text-slate-800'}`}>
        {value}
      </div>
      {hint && <div className='text-xs text-slate-400 mt-0.5'>{hint}</div>}
    </div>
  );
}

export default CompanyArBilling;
